/* The logo, and why it does not live in the store.
 *
 * addFiles() calls uiStore.$reset() every time a picture is staged, and the
 * comments around that line record two bugs that came out of it -- a stray
 * .txt losing a staged image, and blob URLs leaking when the reference went
 * with the reset. A logo has to outlive the picture it is drawn on: "watermark
 * these, one at a time, with my logo" is the entire use, and a logo that
 * vanished on the next image would make the feature pointless.
 *
 * So it is module state, not store state. $reset() cannot reach it, and
 * clearing it is something the visitor asks for rather than something that
 * happens to them.
 */
import { reactive, markRaw } from "vue"
import { DEFAULT_LOGO_SIZE } from "./watermark"

export const logo = reactive({
	// "text" draws the words, "logo" draws the picture. One or the other:
	// offering both at once doubles the placement controls to solve a problem
	// nobody has yet reported having.
	mode: "text",
	sourceUrl: "",
	objectUrl: "",
	image: null,
	width: 0,
	height: 0,
	size: DEFAULT_LOGO_SIZE,
	loading: false,
	error: "",
})

/* Revoking matters here. Every fetch makes an object URL, and the old one is
 * a live reference to decoded image data until it is let go. */
function release() {
	if (logo.objectUrl) {
		try { URL.revokeObjectURL(logo.objectUrl) } catch (e) { /* already gone */ }
	}
	logo.objectUrl = ""
	logo.image = null
	logo.width = 0
	logo.height = 0
}

export function clearLogo() {
	release()
	logo.sourceUrl = ""
	logo.error = ""
	logo.mode = "text"
}

export function hasLogo() {
	return !!(logo.image && logo.width && logo.height)
}

/* Fetch through our own origin, never straight from the source.
 *
 * A logo loaded cross-origin taints the canvas, and the export then throws --
 * the preview looks right and the download fails, which is the worst way for
 * this to break. /fetchLogo hands the bytes back from our own domain, so the
 * canvas stays clean without depending on a CORS header configured at the
 * edge. It also means the logo never becomes a public URL.
 */
export async function loadLogoFromUrl(baseUrl, url) {
	const wanted = (url || "").trim()
	if (!wanted) return false
	logo.loading = true
	logo.error = ""
	try {
		const body = new FormData()
		body.append("urlToFetch", wanted)
		const res = await fetch(`${baseUrl}/fetchLogo`, {method: "POST", body})
		if (!res.ok) {
			let why = ""
			try { why = (await res.json()).Error || "" } catch (e) { /* not json */ }
			logo.error = why || "That logo could not be fetched"
			return false
		}
		const blob = await res.blob()
		const objectUrl = URL.createObjectURL(blob)
		const img = new Image()
		img.src = objectUrl
		await img.decode()
		if (!img.naturalWidth || !img.naturalHeight) {
			URL.revokeObjectURL(objectUrl)
			logo.error = "That logo could not be read"
			return false
		}
		release()
		logo.objectUrl = objectUrl
		// markRaw: an <img> has no business being made deeply reactive, and
		// Vue walking a DOM node is a good way to find a cycle.
		logo.image = markRaw(img)
		logo.width = img.naturalWidth
		logo.height = img.naturalHeight
		logo.sourceUrl = wanted
		logo.mode = "logo"
		return true
	} catch (e) {
		logo.error = "That logo could not be fetched"
		return false
	} finally {
		logo.loading = false
	}
}
